import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  DEFAULT_REQUIREMENT_FORM,
  apply_requirement_to_hierarchy,
  build_hierarchy_cards,
  build_requirement_form,
  format_available_sub_entity,
  normalize_max_count_input,
  parse_response_data
} from './manage_sub_entity_helpers.js';
import {
  CREATE_CHILD_REQUIREMENT_URL,
  CREATE_ENTITY_CONFIG_URL,
  CREATE_SUB_ENTITY_TYPE_URL,
  GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
  LIST_ASSIGNED_CHILD_ENTITY_TYPES_URL,
  UNASSIGN_SUB_ENTITY_TYPE_URL,
  VIEW_ENTITY_TYPE_URL,
  get_request_config
} from '@/utils/system_constants/constant.js';

export default {
  name: 'ManageSubEntities',
  mixins: [alert_mixin],
  props: {
    entityConfigId: {
      type: Number,
      default: null
    }
  },
  data: () => ({
    collapsed_all: false,
    hierarchy_cards: [],
    loading: false,
    load_error: '',
    active_add_child_parent: null,
    selected_sub_entity_id: '',
    sub_entities_loading: false,
    binding_sub_entity: false,
    unbinding_child_key: '',
    available_sub_entities: [],
    creating_requirement: false,
    active_requirement_binding: null,
    active_requirement_parent: null,
    requirement_form: { ...DEFAULT_REQUIREMENT_FORM }
  }),
  mounted() {
    this.initialize_add_child_modal();
    this.initialize_requirement_modal();
    this.fetch_available_sub_entities();
    this.fetch_hierarchy();
  },
  beforeUnmount() {
    this.teardown_add_child_modal();
    this.teardown_requirement_modal();
  },
  computed: {
    selected_parent_entity_type_id() {
      const candidate_ids = [
        Number(this.$route?.params?.entityTypeId || 0),
        Number(this.$route?.query?.entityTypeId || 0)
      ];

      for (const candidate_id of candidate_ids) {
        if (Number.isInteger(candidate_id) && candidate_id > 0) {
          return candidate_id;
        }
      }

      return null;
    },
    entity_type_id() {
      return this.selected_parent_entity_type_id || Number(this.primary_card?.id || 0) || null;
    },
    resolved_entity_config_id() {
      const prop_id = Number(this.entityConfigId);
      if (Number.isInteger(prop_id) && prop_id > 0) {
        return prop_id;
      }

      const route_query_id = Number(this.$route?.query?.entityConfigId || 0);
      return Number.isInteger(route_query_id) && route_query_id > 0 ? route_query_id : null;
    },
    primary_card() {
      return this.hierarchy_cards[0] || null;
    },
    secondary_cards() {
      return this.hierarchy_cards.slice(1);
    },
    bindable_sub_entities() {
      const active_parent_id = String(this.active_add_child_parent?.id || this.primary_card?.id || '');
      const assigned_child_ids = new Set(
        ((this.active_add_child_parent?.children) || (this.primary_card?.children) || [])
          .map((item) => String(item.id || ''))
          .filter(Boolean)
      );

      return (this.available_sub_entities || []).filter(
        (item) => String(item.id) !== active_parent_id && !assigned_child_ids.has(String(item.id))
      );
    },
    hero_metrics() {
      const total_parent_entities = this.hierarchy_cards.length;
      const total_child_entities = this.hierarchy_cards.reduce(
        (total, card) => total + (card.children?.length || 0),
        0
      );

      return [
        {
          label: 'Total Parent Entities',
          value: total_parent_entities,
          icon: 'bi-building',
          tone: 'primary'
        },
        {
          label: 'Total Child Entities',
          value: total_child_entities,
          icon: 'bi-diagram-3',
          tone: 'warning'
        }
      ];
    },
    total_sub_levels() {
      return this.primary_card?.children?.length || 0;
    }
  },
  methods: {
    initialize_add_child_modal() {
      this.add_child_modal_element = document.getElementById('manage_add_child_modal');
      if (!this.add_child_modal_element) {
        return;
      }

      this.handle_add_child_modal_hidden = () => {
        this.active_add_child_parent = null;
        this.selected_sub_entity_id = '';
      };

      this.add_child_modal_element.addEventListener(
        'hidden.bs.modal',
        this.handle_add_child_modal_hidden
      );
    },
    teardown_add_child_modal() {
      if (!this.add_child_modal_element || !this.handle_add_child_modal_hidden) {
        return;
      }

      this.add_child_modal_element.removeEventListener(
        'hidden.bs.modal',
        this.handle_add_child_modal_hidden
      );
    },
    initialize_requirement_modal() {
      this.requirement_modal_element = document.getElementById('manage_child_requirement_modal');
      if (!this.requirement_modal_element) {
        return;
      }

      this.handle_requirement_modal_hidden = () => {
        this.close_requirement_modal();
      };

      this.requirement_modal_element.addEventListener(
        'hidden.bs.modal',
        this.handle_requirement_modal_hidden
      );
    },
    teardown_requirement_modal() {
      if (!this.requirement_modal_element || !this.handle_requirement_modal_hidden) {
        return;
      }

      this.requirement_modal_element.removeEventListener(
        'hidden.bs.modal',
        this.handle_requirement_modal_hidden
      );
    },
    parse_response_data(payload) {
      return parse_response_data(payload);
    },
    get_entity_icon(entity, has_children = false) {
      const name = String(entity?.entity_type_name || '').toLowerCase();
      const icon_rules = [
        {
          icon: 'bi-building',
          matches: has_children || ['trust', 'company', 'entity'].some((keyword) => name.includes(keyword))
        },
        {
          icon: 'bi-file-earmark-text',
          matches: ['document', 'file'].some((keyword) => name.includes(keyword))
        },
        {
          icon: 'bi-people',
          matches: ['beneficiar', 'trustee', 'founder', 'funder'].some((keyword) => name.includes(keyword))
        }
      ];

      const matched_rule = icon_rules.find((rule) => rule.matches);
      if (matched_rule) {
        return matched_rule.icon;
      }

      return 'bi-diagram-3';
    },
    get_entity_tone(entity, has_children = false) {
      const name = String(entity?.entity_type_name || '').toLowerCase();

      if (name.includes('document') || name.includes('file')) {
        return 'highlight';
      }

      return has_children ? 'neutral' : 'neutral';
    },
    build_requirement_payload(entity_config_id) {
      const binding = this.active_requirement_binding;
      const sub_entity_type_id = Number(binding?.sub_entity_type_id);
      const min_count = Number(this.requirement_form.min_count);
      const max_count = normalize_max_count_input(this.requirement_form.max_count);
      const display_order = Number(this.requirement_form.display_order);
      const display_name = String(this.requirement_form.display_name || '').trim();
      const onboarding_mode = String(this.requirement_form.onboarding_mode || '').trim();

      if (!Number.isInteger(entity_config_id) || entity_config_id <= 0) {
        this.show_error('Unable to determine the current entity configuration.');
        return null;
      }

      if (!Number.isInteger(sub_entity_type_id) || sub_entity_type_id <= 0) {
        this.show_error('Unable to determine the selected child entity type.');
        return null;
      }

      if (!display_name) {
        this.show_error('Please enter a display name for this requirement.');
        return null;
      }

      if (!Number.isInteger(min_count) || min_count < 0) {
        this.show_error('Minimum count must be a valid number greater than or equal to 0.');
        return null;
      }

      if (max_count !== null && (!Number.isInteger(max_count) || max_count < min_count)) {
        this.show_error('Maximum count must be blank for no maximum or a valid number greater than or equal to minimum count.');
        return null;
      }

      if (!onboarding_mode) {
        this.show_error('Please select an onboarding mode.');
        return null;
      }

      if (!Number.isInteger(display_order) || display_order < 0) {
        this.show_error('Display order must be a valid number greater than or equal to 0.');
        return null;
      }

      const requirement_payload = {
        entity_config_id,
        sub_entity_type_id,
        display_name,
        min_count,
        max_count,
        onboarding_mode,
        is_required_for_completion: Boolean(this.requirement_form.is_required_for_completion),
        display_order
      };

      return requirement_payload;
    },
    build_sub_entity_payload() {
      const parent_id = Number(this.active_add_child_parent?.id || this.entity_type_id);
      const child_id = Number(this.selected_sub_entity_id);

      if (!Number.isInteger(parent_id) || parent_id <= 0) {
        this.show_error('Unable to determine the current parent entity type.');
        return null;
      }

      if (!Number.isInteger(child_id) || child_id <= 0) {
        this.show_error('Please select a valid child entity type.');
        return null;
      }

      const payload = { parent_id, child_id };
      return payload;
    },
    build_unassign_payload(parent_card, child) {
      const parent_id = Number(parent_card?.id || 0);
      const child_id = Number(child?.id || child?.child_entity_type_id || 0);

      if (!Number.isInteger(parent_id) || parent_id <= 0) {
        this.show_error('Unable to determine the current parent entity type.');
        return null;
      }

      if (!Number.isInteger(child_id) || child_id <= 0) {
        this.show_error('Unable to determine the selected child entity type.');
        return null;
      }

      const payload = { parent_id, child_id };
      return payload;
    },
    async fetch_full_config(target_entity_type_id = this.entity_type_id) {
      const response = await axios.get(
        GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
        get_request_config({
          params: {
            entity_type_id: target_entity_type_id
          }
        })
      );
      const response_data = this.parse_response_data(response.data);
      return response_data.data || {};
    },
    async ensure_entity_config_exists(target_entity_type_id = this.entity_type_id, resolved_entity_config_id = this.resolved_entity_config_id) {
      if (resolved_entity_config_id) {
        return resolved_entity_config_id;
      }

      if (!Number.isInteger(target_entity_type_id) || target_entity_type_id <= 0) {
        return;
      }

      const config_data = await this.fetch_full_config(target_entity_type_id).catch(async () => {

        await axios.post(
          CREATE_ENTITY_CONFIG_URL,
          {
            entity_type_id: target_entity_type_id
          },
          get_request_config()
        );

        return this.fetch_full_config(target_entity_type_id);
      });

      const entity_config_id = Number(config_data?.entity_config?.id);
      if (!Number.isInteger(entity_config_id) || entity_config_id <= 0) {
        return;
      }

      return entity_config_id;
    },
    async fetch_available_sub_entities() {
      this.sub_entities_loading = true;

      try {
        const response = await axios.get(VIEW_ENTITY_TYPE_URL, get_request_config());
        const response_data = this.parse_response_data(response.data);
        const response_items = Array.isArray(response_data.data) ? response_data.data : [];

        this.available_sub_entities = response_items
          .map((item, index) => format_available_sub_entity(item, index))
          .filter((item) => item.id);
      } catch {
        this.available_sub_entities = [];
      } finally {
        this.sub_entities_loading = false;
      }
    },
    async fetch_assigned_children(parent_entity_type_id) {
      const response = await axios.get(
        LIST_ASSIGNED_CHILD_ENTITY_TYPES_URL,
        get_request_config({
          params: { parent_entity_type_id }
        })
      ).catch(() => null);

      if (!response) {
        const assigned_children = [];
        return assigned_children;
      }

      const response_data = this.parse_response_data(response.data);
      const assigned_children = Array.isArray(response_data.data) ? response_data.data : [];

      return assigned_children;
    },
    async fetch_hierarchy() {
      this.loading = true;
      this.load_error = '';

      try {
        const entities_response = await axios.get(
          VIEW_ENTITY_TYPE_URL,
          get_request_config()
        );
        const entities_payload = this.parse_response_data(entities_response.data);
        const entities = Array.isArray(entities_payload.data) ? entities_payload.data : [];

        const assigned_children_results = await Promise.all(
          entities.map(async (entity) => ([
            Number(entity.id),
            await this.fetch_assigned_children(Number(entity.id))
          ]))
        );

        const assigned_children_by_parent_id = new Map(assigned_children_results);
        this.hierarchy_cards = build_hierarchy_cards(
          entities,
          assigned_children_by_parent_id,
          this.selected_parent_entity_type_id,
          this.collapsed_all
        );
      } catch (error) {
        const response_data = this.parse_response_data(error?.response?.data || {});
        this.hierarchy_cards = [];
        this.load_error = response_data.message || 'Failed to load entities.';
      } finally {
        this.loading = false;
      }
    },
    async bind_sub_entity() {
      const payload = this.build_sub_entity_payload();
      if (!payload) {
        return;
      }

      const selected = this.available_sub_entities.find((item) => item.id === this.selected_sub_entity_id);
      if (!selected) {
        this.show_error('The selected child entity type could not be found.');
        return;
      }

      const already_bound = ((this.active_add_child_parent?.children) || []).some(
        (item) => String(item.id) === String(selected.id)
      );
      if (already_bound) {
        this.selected_sub_entity_id = '';
        this.show_error('This child entity type has already been assigned.');
        return;
      }

      this.binding_sub_entity = true;

      try {
        const response = await axios.post(
          CREATE_SUB_ENTITY_TYPE_URL,
          payload,
          get_request_config()
        );
        const response_data = this.parse_response_data(response.data);
        this.selected_sub_entity_id = '';
        await this.fetch_hierarchy();
        this.show_success(response_data.message || 'Child entity assigned successfully.');
        this.close_modal('manage_add_child_modal');
      } catch (error) {
        const response_data = this.parse_response_data(error?.response?.data || {});
        this.show_error(response_data.message || 'Failed to assign child entity.');
      } finally {
        this.binding_sub_entity = false;
      }
    },
    open_requirement_modal(binding, index = 0, parent_card = null) {
      this.active_requirement_binding = binding;
      this.active_requirement_parent = parent_card;
      this.requirement_form = build_requirement_form(binding?.name, index);
    },
    close_requirement_modal() {
      this.active_requirement_binding = null;
      this.active_requirement_parent = null;
      this.requirement_form = build_requirement_form();
    },
    apply_requirement_to_child(child_id, created_requirement, payload) {
      this.hierarchy_cards = apply_requirement_to_hierarchy(
        this.hierarchy_cards,
        child_id,
        created_requirement,
        payload
      );
    },
    build_card_without_child(card, child_id) {
      const updated_children = (card.children || []).filter(
        (item) => String(item.id) !== String(child_id)
      );
      const updated_card = {
        ...card,
        children: updated_children,
        child_summary: `${updated_children.length} Child Entities`
      };

      return updated_card;
    },
    async create_requirement() {
      let entity_config_id = null;

      try {
        entity_config_id = await this.ensure_entity_config_exists(
          Number(this.active_requirement_parent?.id || this.entity_type_id || 0),
          Number(this.active_requirement_parent?.id || 0) === Number(this.entity_type_id || 0)
            ? this.resolved_entity_config_id
            : null
        );
      } catch (error) {
        const response_data = this.parse_response_data(error?.response?.data || {});
        this.show_error(response_data.message || 'Failed to prepare entity configuration for child requirements.');
        return;
      }

      const payload = this.build_requirement_payload(entity_config_id);
      if (!payload) {
        return;
      }

      this.creating_requirement = true;

      try {
        const response = await axios.post(
          CREATE_CHILD_REQUIREMENT_URL,
          payload,
          get_request_config()
        );
        const response_data = this.parse_response_data(response.data);
        const created_requirement = response_data.data || {};

        this.apply_requirement_to_child(this.active_requirement_binding?.id, created_requirement, payload);
        this.show_success(response_data.message || 'Child requirement created successfully.');
        this.close_modal('manage_child_requirement_modal');
      } catch (error) {
        const response_data = this.parse_response_data(error?.response?.data || {});
        this.show_error(response_data.message || 'Failed to create child requirement.');
      } finally {
        this.creating_requirement = false;
      }
    },
    toggle_card(card_id) {
      this.hierarchy_cards = this.hierarchy_cards.map((card) => {
        if (card.id !== card_id) {
          return card;
        }

        const updated_card = { ...card, expanded: !card.expanded };
        return updated_card;
      });
    },
    toggle_child(card_id, child_id) {
      this.hierarchy_cards = this.hierarchy_cards.map((card) => {
        if (card.id !== card_id) {
          return card;
        }

        const updated_children = (card.children || []).map((child) => {
          if (child.id !== child_id) {
            return child;
          }

          const updated_child = {
            ...child,
            expanded: child.expandable ? !child.expanded : child.expanded
          };

          return updated_child;
        });
        const updated_card = {
          ...card,
          children: updated_children
        };

        return updated_card;
      });
    },
    async unassign_child_entity(parent_card, child) {
      const payload = this.build_unassign_payload(parent_card, child);
      if (!payload) {
        return;
      }

      this.unbinding_child_key = `${parent_card?.id}-${child?.id}`;

      try {
        const response = await axios.post(
          UNASSIGN_SUB_ENTITY_TYPE_URL,
          payload,
          get_request_config()
        );
        const response_data = this.parse_response_data(response.data);

        this.hierarchy_cards = this.hierarchy_cards.map((card) =>
          String(card.id) !== String(parent_card?.id)
            ? card
            : this.build_card_without_child(card, child?.id)
        );

        this.show_success(response_data.message || 'Child entity unassigned successfully.');
      } catch (error) {
        const response_data = this.parse_response_data(error?.response?.data || {});
        this.show_error(response_data.message || 'Failed to unassign child entity.');
      }

      this.unbinding_child_key = '';
    },
    confirm_unassign_child(parent_card, child) {
      this.show_confirm(
        'Unassign Child Entity',
        `Are you sure you want to unassign ${child?.name || 'this child entity'} from ${parent_card?.name || 'this parent'}?`,
        () => {
          this.unassign_child_entity(parent_card, child);
        }
      );
    },
    add_sub_entity(card = null) {
      this.active_add_child_parent = card;
      this.selected_sub_entity_id = '';
    },
    go_to_child_requirements(card = null) {
      const target_entity_type_id = Number(card?.id || this.entity_type_id || 0);
      const query = {
        entityTypeId: target_entity_type_id ? String(target_entity_type_id) : ''
      };
      const route = {
        name: 'SubEntityRequirements',
        query
      };

      this.$router.push(route);
    }
  }
};
